import { useState, useEffect, useCallback } from 'react'
import {
    Shield,
    ChevronDown, ChevronUp, Search, Loader2, AlertCircle,
} from 'lucide-react'

import ComplianceDocsPanel, { getAuthHeaders } from '../../enrollment/components/ComplianceDocsPanel'

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api'

// ─── Main component ──────────────────────────────────────────────────────────
export default function ApplicationDataLogs() {
    const [applications, setApplications] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [total, setTotal] = useState(0)
    const [expandedRow, setExpandedRow] = useState<string | null>(null)

    const fetchData = useCallback(async (searchVal: string, pageVal: number) => {
        setLoading(true)
        setError(null)
        try {
            const params = new URLSearchParams({
                page: String(pageVal),
                limit: '15',
                ...(searchVal ? { search: searchVal } : {})
            })
            const resp = await fetch(`${API_BASE_URL}/application-data-logs?${params}`, {
                headers: getAuthHeaders()
            })
            if (!resp.ok) throw new Error('Failed to load')
            const data = await resp.json()
            if (data.success) {
                setApplications(data.data)
                setTotalPages(data.pagination.totalPages)
                setTotal(data.pagination.total)
            }
        } catch (e: any) {
            setError(e.message || 'Failed to load application data logs')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        const timer = setTimeout(() => fetchData(search, page), 300)
        return () => clearTimeout(timer)
    }, [search, page, fetchData])

    const completionColor = (count: number) => {
        if (count === 11) return 'text-green-600 bg-green-50'
        if (count >= 6) return 'text-yellow-600 bg-yellow-50'
        if (count >= 1) return 'text-orange-600 bg-orange-50'
        return 'text-red-600 bg-red-50'
    }

    const complaintCompletionColor = (count: number) => {
        if (count === 7) return 'text-green-600 bg-green-50'
        if (count >= 4) return 'text-yellow-600 bg-yellow-50'
        if (count >= 1) return 'text-orange-600 bg-orange-50'
        return 'text-gray-600 bg-gray-50'
    }

    return (
        <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mb-6 p-6">
                <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                        <Shield className="w-6 h-6 text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Application Data Logs</h1>
                        <p className="text-sm text-gray-500">Compliance document vault for all submitted applications</p>
                    </div>
                </div>

                {/* Stats banner */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-indigo-50 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-indigo-700">{total}</div>
                        <div className="text-xs text-indigo-600 font-medium">Total Applications</div>
                    </div>
                    <div className="bg-green-50 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-green-700">
                            {applications.filter(a => a.documentsComplete === 11).length}
                        </div>
                        <div className="text-xs text-green-600 font-medium">Fully Complete (11/11)</div>
                    </div>
                    <div className="bg-red-50 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-red-700">
                            {applications.filter(a => a.documentsComplete < 11).length}
                        </div>
                        <div className="text-xs text-red-600 font-medium">Incomplete Documents</div>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-4 text-center">
                        <div className="text-2xl font-bold text-amber-700">
                            {applications.filter(a => (a.complaintEvidenceComplete || 0) > 0).length}
                        </div>
                        <div className="text-xs text-amber-600 font-medium">Complaint Evidence Started</div>
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-4 px-4 py-3 flex items-center gap-3">
                <Search className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <input
                    type="text"
                    placeholder="Search by client name or agent name..."
                    value={search}
                    onChange={e => { setSearch(e.target.value); setPage(1) }}
                    className="flex-1 outline-none text-sm text-gray-700 placeholder-gray-400"
                />
                {loading && <Loader2 className="w-4 h-4 animate-spin text-blue-400" />}
            </div>

            {/* Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Table Header */}
                <div className="bg-gray-50 border-b border-gray-200 px-6 py-3">
                    <div className="grid grid-cols-12 gap-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        <div className="col-span-3">Client</div>
                        <div className="col-span-2">Agent</div>
                        <div className="col-span-2">Campaign</div>
                        <div className="col-span-2">Submitted</div>
                        <div className="col-span-1 text-center">Core (11)</div>
                        <div className="col-span-1 text-center">Complaint (7)</div>
                        <div className="col-span-1"></div>
                    </div>
                </div>

                {error ? (
                    <div className="flex items-center gap-3 p-8 text-red-600">
                        <AlertCircle className="w-5 h-5" />
                        <span>{error}</span>
                    </div>
                ) : loading && applications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20">
                        <Loader2 className="w-10 h-10 animate-spin text-indigo-600" />
                        <p className="mt-4 text-gray-500 font-medium">Fetching application logs...</p>
                    </div>
                ) : applications.length === 0 ? (
                    <div className="text-center py-16 text-gray-400">
                        <Shield className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p className="font-medium">No completed applications found</p>
                        {search && <p className="text-sm mt-1">Try a different search term</p>}
                    </div>
                ) : (
                    <div className={`divide-y divide-gray-100 ${loading ? 'opacity-50' : ''}`}>
                        {applications.map((app, idx) => {
                            const isExpanded = expandedRow === app.enrollmentSessionId
                            const status = app.complianceStatus || {}
                            const completedCount = app.documentsComplete || 0
                            const complaintCount = app.complaintEvidenceComplete || 0

                            return (
                                <div key={app.enrollmentSessionId}>
                                    <div
                                        className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                                        onClick={() => setExpandedRow(isExpanded ? null : app.enrollmentSessionId)}
                                    >
                                        <div className="grid grid-cols-12 gap-4 items-center">
                                            {/* Client */}
                                            <div className="col-span-3">
                                                <div className="font-semibold text-gray-900 text-sm">{app.clientName}</div>
                                                <div className="text-xs text-gray-400 truncate">{app.clientEmail}</div>
                                            </div>

                                            {/* Agent */}
                                            <div className="col-span-2 text-sm text-gray-700">{app.agentName}</div>

                                            {/* Campaign */}
                                            <div className="col-span-2">
                                                <div className="text-sm font-medium text-gray-900 truncate" title={app.campaignId}>
                                                    {app.campaignName}
                                                </div>
                                                <div className="text-[10px] text-gray-400 font-mono tracking-tighter">
                                                    {app.campaignId?.substring(0, 8)}...
                                                </div>
                                            </div>

                                            {/* Submitted */}
                                            <div className="col-span-2 text-xs text-gray-500">
                                                {app.submittedAt ? new Date(app.submittedAt).toLocaleDateString() : '—'}
                                            </div>

                                            {/* Document completion */}
                                            <div className="col-span-1 flex flex-col items-center gap-1.5">
                                                <span className={`text-sm font-bold px-3 py-1 rounded-full ${completionColor(completedCount)}`}>
                                                    {completedCount}/11
                                                </span>
                                                {/* Mini dot grid */}
                                                <div className="flex flex-wrap gap-1 justify-center max-w-[88px]">
                                                    {Object.values(status).map((v: any, i: number) => (
                                                        <div key={i} className={`w-2 h-2 rounded-full ${v ? 'bg-green-500' : 'bg-red-300'}`} />
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="col-span-1 flex justify-center">
                                                <span className={`text-sm font-bold px-3 py-1 rounded-full ${complaintCompletionColor(complaintCount)}`}>
                                                    {complaintCount}/7
                                                </span>
                                            </div>

                                            {/* Expand toggle */}
                                            <div className="col-span-1 flex justify-end">
                                                {isExpanded
                                                    ? <ChevronUp className="w-4 h-4 text-gray-400" />
                                                    : <ChevronDown className="w-4 h-4 text-gray-400" />
                                                }
                                            </div>
                                        </div>
                                    </div>

                                    {/* Expanded compliance panel */}
                                    {isExpanded && (
                                        <ComplianceDocsPanel sessionId={app.enrollmentSessionId} />
                                    )}
                                </div>
                            )
                        })}
                    </div>
                )}

                {/* Pagination */}
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                    <div className="text-sm text-gray-600">
                        Showing <span className="font-semibold text-gray-900">{(page - 1) * 15 + 1}</span> to <span className="font-semibold text-gray-900">{Math.min(page * 15, total)}</span> of <span className="font-semibold text-gray-900">{total}</span> results
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                        >Previous</button>
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
                        >Next</button>
                    </div>
                </div>
            </div>
        </div>
    )
}
